// src/services/itinerary/previewApi.js
// Frontend client for the live 1.5-day itinerary preview.
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { getRuntimeIslandKey } from '../../lib/siteContext';

const HST_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: 'Pacific/Honolulu'
});

function formatTimeRange(startISO, endISO) {
  if (!startISO || !endISO) return null;
  try {
    const start = new Date(startISO);
    const end = new Date(endISO);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return null;
    const startStr = HST_TIME_FORMATTER.format(start);
    const endStr = HST_TIME_FORMATTER.format(end);
    return `${startStr} – ${endStr}`;
  } catch {
    return null;
  }
}

function diffMinutes(startISO, endISO) {
  if (!startISO || !endISO) return null;
  const start = Date.parse(startISO);
  const end = Date.parse(endISO);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.max(15, Math.round((end - start) / 60000));
}

function buildActivityFromBlock(block) {
  const durationMin = diffMinutes(block?.schedule?.startISO, block?.schedule?.endISO);
  const rationale = typeof block?.guidance?.rationale === 'string' ? block.guidance.rationale.trim() : '';
  return {
    title: block?.selection?.entity?.title || block?.label,
    duration: durationMin ? `${durationMin} min` : undefined,
    whyForYou: rationale ? rationale : undefined,
  };
}

function mapPlannerBlock(block) {
  const statusTag = block.status !== 'planned' ? ' (needs selection)' : '';
  const timeframe = formatTimeRange(block?.schedule?.startISO, block?.schedule?.endISO);
  const instructions = typeof block?.guidance?.instructions === 'string' ? block.guidance.instructions.trim() : '';
  const details = instructions || block?.selection?.summary || null;
  const activity = buildActivityFromBlock(block);
  const logistics = block?.selection?.reasoning ? { tip: block.selection.reasoning } : undefined;
  const relatedEntities = Array.isArray(block?.relatedEntities)
    ? block.relatedEntities
        .slice(0, 3)
        .map((entity) => ({
          title: entity?.title || entity?.matchedName || entity?.slug || '',
          url: entity?.url || '',
          catalog: entity?.catalog,
          slug: entity?.slug,
          similarity: entity?.similarity,
          matchConfidence: entity?.matchConfidence,
          matchedName: entity?.matchedName || null
        }))
        .filter((entity) => entity.title && entity.url && entity.slug && entity.catalog)
    : [];
  return {
    title: `${block.label}${statusTag}`,
    timeframe,
    details,
    activities: activity.whyForYou ? [activity] : [],
    logistics,
    relatedEntities,
  };
}

function mapPlannerDay(day) {
  if (!day) return null;
  const dayNumber = typeof day?.profile?.dayIndex === 'number' ? day.profile.dayIndex : (typeof day?.dayIndex === 'number' ? day.dayIndex : 0);
  const title = day?.profile?.label || (dayNumber === 0 ? 'Arrival Day' : `Day ${dayNumber}`);
  const summary = (day?.skeleton?.theme && day.skeleton.theme.trim()) || (day?.profile?.teaser && day.profile.teaser.trim()) || day?.summary;
  const blocks = Array.isArray(day?.blocks) ? day.blocks.map(mapPlannerBlock) : [];
  const calendarDate = day?.profile?.calendarDate || day?.calendarDate;
  const calendarPrecision = day?.profile?.calendarPrecision || day?.calendarPrecision;
  const iteration = day?.iteration && typeof day.iteration === 'object' ? day.iteration : null;
  return {
    dayNumber,
    title,
    summary,
    blocks,
    issues: day?.issues || [],
    calendarDate,
    calendarPrecision,
    ...(iteration ? { iteration } : {}),
  };
}

function mapPlannerDays(rawDays) {
  if (!rawDays) return [];
  let daysArray = [];
  if (Array.isArray(rawDays)) {
    daysArray = rawDays;
  } else if (typeof rawDays === 'object' && rawDays !== null) {
    daysArray = Object.values(rawDays);
  }
  return daysArray
    .map(mapPlannerDay)
    .filter(Boolean)
    .sort((a, b) => a.dayNumber - b.dayNumber);
}

// Live backend wiring
export async function startPreview(interviewId) {
  const response = await fetch('https://us-central1-alakaialohaadventures-782c9.cloudfunctions.net/itineraryGenerate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: { interviewId, islandKey: getRuntimeIslandKey() }
    })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

// Listen to a version doc and stream progressive updates. Returns unsubscribe fn.
export function listenPreviewVersion(interviewId, versionId, onData, onError) {
  const ref = doc(db, 'itineraries', interviewId, 'versions', versionId);
  let first = true;
  const unsub = onSnapshot(ref, (snap) => {
    if (!snap.exists()) return;
    const d = snap.data() || {};
    const context = d.travelerContext ? {
      party: d.travelerContext.party,
      partyInsights: d.travelerContext.partyInsights,
      dates: d.travelerContext.dates,
      homebase: d.travelerContext.homebase,
      preferences: d.travelerContext.preferences,
      specialConsiderations: d.travelerContext.specialConsiderations,
    } : undefined;

    let previewDaysAll = mapPlannerDays(d.plannerPreview?.days);
    if (previewDaysAll.length === 0) {
      const fallback = [];
      if (d.arrivalDay) {
        const day0 = { ...d.arrivalDay };
        if (day0.dayNumber === undefined) day0.dayNumber = 0;
        fallback.push(mapPlannerDay(day0));
      }
      if (d.day1) {
        const day1 = { ...d.day1 };
        if (day1.dayNumber === undefined) day1.dayNumber = 1;
        fallback.push(mapPlannerDay(day1));
      }
      previewDaysAll = fallback.filter(Boolean).sort((a, b) => a.dayNumber - b.dayNumber);
    }
    const previewDays = previewDaysAll.filter(day => day.dayNumber <= 1).slice(0, 2);
    const fullDays = mapPlannerDays(d.plannerFull?.days);
    const renderedDays = fullDays.length > 0
      ? fullDays
      : (previewDays.length > 0 ? previewDays : previewDaysAll);

    onData({
      context,
      days: renderedDays,
      previewDays,
      fullDays,
      thesis: d.tripThesis,
      teasers: d.tripTeasers,
      synthesisSnapshot: d.synthesisSnapshot || null,
      meta: {
        interviewId,
        versionId,
        status: d.status,
        completedSections: d.completedSections || [],
        accessLevel: d.accessLevel || 'preview',
        unlockSource: d.unlockSource || null,
        generationStatus: d.generationStatus || d.status || 'preview',
        generationError: d.generationError || d.error || null,
        plannerFullStatus: d.plannerFull?.status,
        plannerPreviewStatus: d.plannerPreview?.status,
        plannerFullLatestDayIndex: d.plannerFull?.latestDayIndex,
        editPolicy: d.editPolicy || null,
        regeneration: d.regeneration || null,
        previewRegeneration: d.previewRegeneration || null,
        regenerating: d.regenerating || null,
      },
    }, first);
    first = false;
  }, (err) => {
    if (onError) onError(err);
  });
  return unsub;
}

const previewApi = { startPreview, listenPreviewVersion };
export default previewApi;
